/**
 * Operator Feedback Hooks (Phase 21.9)
 *
 * TanStack Query hooks for operator feedback CRUD.
 *
 * - useCheckFeedback(targetId, type) -- GET existing feedback for a target
 * - useSubmitFeedback() -- POST mutation to submit new feedback
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../lib/hooks/useApi';
import type {
  OperatorFeedbackResponse,
  SubmitOperatorFeedback,
  OperatorFeedbackType,
} from '@guild/shared';

// ============================================
// QUERY KEYS
// ============================================

export const operatorFeedbackKeys = {
  all: ['operator-feedback'] as const,
  check: (targetId: string, type: string) =>
    [...operatorFeedbackKeys.all, 'check', targetId, type] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Check if the operator already submitted feedback for a given target.
 * Returns the existing feedback or null.
 */
export function useCheckFeedback(targetId: string | undefined, type: OperatorFeedbackType) {
  const api = useApi();

  return useQuery({
    queryKey: operatorFeedbackKeys.check(targetId ?? '', type),
    queryFn: () =>
      api.get<OperatorFeedbackResponse | null>(
        `/api/operator/feedback?targetId=${targetId}&type=${type}`
      ),
    enabled: !!targetId,
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Submit new feedback. Invalidates the check query on success.
 */
export function useSubmitFeedback() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SubmitOperatorFeedback) =>
      api.post<OperatorFeedbackResponse>('/api/operator/feedback', data),
    onSuccess: (_data, variables) => {
      // Invalidate the specific check query so it refetches
      queryClient.invalidateQueries({
        queryKey: operatorFeedbackKeys.check(variables.targetId, variables.feedbackType),
      });
    },
  });
}
